<script lang="ts" setup>
import AUApi from "@/api";
import { ref, onMounted, reactive, nextTick } from "vue";
import { Globals } from "@/Globals";
import {
  globalsBezeichnung,
  begriffBezeichnung,
  globalsKennung,
  globalsAic,
  globalsBegriffMemo,
  checkBegriff,
} from "@/local";
import {
  getKennungTitle,
  getKZValueDisplay,
  getKennungValueDisplay,
  getKZValue,
} from "@/DataHelper";
import { mountedBase, BaseSetup, reloadData } from "@/DataLoaderStatic";
import CalcButton from "@/components/CalcButton.vue";
import { getSiteData } from "@/NavigationHelper";

const emit = defineEmits(["back"]);
const siteData = getSiteData();
let showCalc = ref<boolean>(false);
let showEssen = ref<boolean>(false);
let showKredit = ref<boolean>(false);
let showKst = ref<boolean>(false);
let showBezahlt = ref<boolean>(false);
let day = ref<string>(siteData.zeitbereich.von.toLocaleDateString());
let name = ref<string>("");
let belegeAzure = false;

const pageParams: DataPageParameter = {
  zeitbereich: AUApi.Current.zeitbereich,
  insertMode: false,
  query: "Web_Detail_VG_SonstigeBelege",
  emitBack: () => emit("back"),
};
const uiData = reactive<DataPageUIData>(BaseSetup(pageParams));

function applyRowState() {
  if (!uiData.showLoading && uiData.data) {
    const row = uiData.data[0];
    if (row) {
      if (belegeAzure) {
        day.value = getKennungValueDisplay(row, "INVOICE_DATE", uiData.columns);
        name.value = getKennungValueDisplay(row, "BEZEICHNUNG", uiData.columns);
      } else {
        day.value = getKennungValueDisplay(row, "VALIDITY", uiData.columns);
        name.value = getKZValueDisplay(row, "name", uiData.columns);
        showBezahlt.value = !!getKennungTitle("RK_NOTPAYED", uiData.columns);
        showKredit.value = !!getKennungTitle(
          "RK_FIRMENKREDITKARTE",
          uiData.columns,
        );
        showEssen.value = !!getKennungTitle("RK_ANZ_PERS", uiData.columns);
        showKst.value = !!getKennungTitle("COST_CENTRE", uiData.columns);
      }

      showCalc.value = getKZValue(row, "showCalc", uiData.columns);
      return;
    }
  }

  showCalc.value = false;
}

onMounted(async () => {
  if (checkBegriff("RK_web_WF_VG_sonstBelegeKI")) {
    pageParams.query = "WEB_VG_StammBelegKI_Freigabe";
    belegeAzure = true;
  }
  await mountedBase(uiData, pageParams);

  applyRowState();
  uiData.showLoading = false;
  uiData.initialized = true;
});

async function onCalculated() {
  siteData.onReloadData = true;
  pageParams.emitBack();
  // 22.5.2026 Doris möchte das die Maske sich nach der Berechnung gleich schliesst!
  // await reloadData(uiData, pageParams);
  // applyRowState();
}
</script>
<template>
  <v-card :loading="uiData.showLoading" v-if="uiData.initialized">
    <v-card-title>
      {{ name }}:
      {{ day }}
    </v-card-title>
    <v-divider class="pa-3"></v-divider>
    <v-card-text class="cardText" v-if="!belegeAzure">
      <v-row>
        <v-col class="dvhCol">
          <span style="white-space: nowrap"
            >{{ getKennungTitle("RK_ARTEN", uiData.columns) }}:</span
          >
        </v-col>
        <v-col class="dvhCol">
          <span style="white-space: nowrap">{{
            getKennungValueDisplay(uiData.data[0], "RK_ARTEN", uiData.columns)
          }}</span>
        </v-col>
      </v-row>
      <v-row class="rowGap">
        <v-col class="dvhCol">
          <span style="white-space: nowrap"
            >{{ getKennungTitle("RK_GEBUEHRENREGION", uiData.columns) }}:</span
          >
        </v-col>
        <v-col class="dvhCol">
          <span style="white-space: nowrap">{{
            getKennungValueDisplay(
              uiData.data[0],
              "RK_GEBUEHRENREGION",
              uiData.columns,
            )
          }}</span>
        </v-col>
      </v-row>
      <v-row class="rowGap">
        <v-col class="dvhCol">
          <span style="white-space: nowrap"
            >{{ getKennungTitle("ALL_ROUND_PRICE", uiData.columns) }}:</span
          >
        </v-col>
        <v-col class="dvhCol">
          <span style="white-space: nowrap">{{
            getKennungValueDisplay(
              uiData.data[0],
              "ALL_ROUND_PRICE",
              uiData.columns,
            )
          }}</span>
        </v-col>
      </v-row>
      <v-row class="rowGap" v-if="showKst">
        <v-col class="dvhCol">
          <span style="white-space: nowrap"
            >{{ getKennungTitle("COST_CENTRE", uiData.columns) }}:</span
          >
        </v-col>
        <v-col class="dvhCol">
          <span style="white-space: nowrap">{{
            getKennungValueDisplay(
              uiData.data[0],
              "COST_CENTRE",
              uiData.columns,
            )
          }}</span>
        </v-col>
      </v-row>

      <EditSlider
        v-if="showEssen"
        :disabled="true"
        class="mt-5"
        kennung="RK_ANZ_PERS"
      />
      <EditSwitchButton
        :disabled="true"
        v-if="showBezahlt"
        class="mt-5"
        kennung="RK_NOTPAYED"
      />
      <EditSwitchButton
        :disabled="true"
        v-if="showKredit"
        kennung="RK_FIRMENKREDITKARTE"
      />
      <EditTextArea :disabledMounted="true" kennung="MEMO" />
      <EditFileChoicer
        class="pl-2"
        :disabled="true"
        kennung="DOKUMENT_DATENBANK"
      />
    </v-card-text>
    <v-card-text class="cardText" v-if="belegeAzure">
      <v-row class="rowGap">
        <v-col class="dvhCol">
          <span style="white-space: nowrap"
            >{{ getKennungTitle("MA_RELATION", uiData.columns) }}:</span
          >
        </v-col>
        <v-col class="dvhCol">
          <span style="white-space: nowrap">{{
            getKennungValueDisplay(
              uiData.data[0],
              "MA_RELATION",
              uiData.columns,
            )
          }}</span>
        </v-col>
      </v-row>
      <v-row class="rowGap">
        <v-col class="dvhCol">
          <span style="white-space: nowrap"
            >{{
              getKennungTitle("KOSTENSTELLENZUORDNG", uiData.columns)
            }}:</span
          >
        </v-col>
        <v-col class="dvhCol">
          <span style="white-space: nowrap">{{
            getKennungValueDisplay(
              uiData.data[0],
              "KOSTENSTELLENZUORDNG",
              uiData.columns,
            )
          }}</span>
        </v-col>
      </v-row>

      <v-row class="rowGap">
        <v-col class="dvhCol">
          <span style="white-space: nowrap"
            >{{ getKennungTitle("ALL_ROUND_PRICE", uiData.columns) }}:</span
          >
        </v-col>
        <v-col class="dvhCol">
          <span style="white-space: nowrap">{{
            getKennungValueDisplay(
              uiData.data[0],
              "ALL_ROUND_PRICE",
              uiData.columns,
            )
          }}</span>
        </v-col>
      </v-row>
      <v-row class="rowGap">
        <v-col class="dvhCol">
          <span style="white-space: nowrap"
            >{{ getKennungTitle("RK_LAND", uiData.columns) }}:</span
          >
        </v-col>
        <v-col class="dvhCol">
          <span style="white-space: nowrap">{{
            getKennungValueDisplay(uiData.data[0], "RK_LAND", uiData.columns)
          }}</span>
        </v-col>
      </v-row>
      <v-row class="rowGap">
        <v-col class="dvhCol">
          <span style="white-space: nowrap"
            >{{ getKennungTitle("STATUSZAHLUNG", uiData.columns) }}:</span
          >
        </v-col>
        <v-col class="dvhCol">
          <span style="white-space: nowrap">{{
            getKennungValueDisplay(
              uiData.data[0],
              "STATUSZAHLUNG",
              uiData.columns,
            )
          }}</span>
        </v-col>
      </v-row>

      <EditTextArea :disabledMounted="true" class="pt-10" kennung="MEMO_MEMO" />
      <EditFileChoicer :disabled="true" kennung="ABLAGE" />
    </v-card-text>

    <v-card-actions class="pa-0 justify-end" v-if="!belegeAzure">
      <CalcButton
        v-if="showCalc"
        modell="RK_Web_SonsBelegFreigabe_VG"
        color="success"
        icon="mdi-check"
        :zeitbereich="pageParams.zeitbereich"
        :text="
          globalsBezeichnung(
            Globals.Begriff,
            'RK_Web_SonsBelegFreigabe_VG',
            'bewilligt',
          )
        "
        variant="plain"
        :modellAic="uiData.data[0].aic_Bew_pool"
        @calculated="onCalculated"
        :bewAic="uiData.data[0].aic_Bew_pool"
      />
      <CalcButton
        v-if="showCalc"
        modell="RK_Web_SonstigerBeleg_abgelehntVG"
        color="error"
        icon="mdi-close"
        class="mr-2"
        :zeitbereich="uiData.zeitbereich"
        :text="
          globalsBezeichnung(
            Globals.Begriff,
            'RK_Web_SonstigerBeleg_abgelehntVG',
            'abgelehnt',
          )
        "
        variant="plain"
        :modellAic="uiData.data[0].aic_Bew_pool"
        @calculated="onCalculated"
        :bewAic="uiData.data[0].aic_Bew_pool"
      />
    </v-card-actions>
    <v-card-actions class="pa-0 justify-end" v-else>
      <CalcButton
        v-if="showCalc"
        modell="RK_Web_SonstigerBelegKI_FreigabeVG"
        color="success"
        icon="mdi-check"
        :zeitbereich="pageParams.zeitbereich"
        :text="
          globalsBezeichnung(
            Globals.Begriff,
            'RK_Web_SonstigerBelegKI_FreigabeVG',
            'bewilligt',
          )
        "
        variant="plain"
        :modellAic="uiData.data[0].aic_Stamm"
        @calculated="onCalculated"
      />
      <CalcButton
        v-if="showCalc"
        modell="RK_Web_SonstBelegKI_AblehnVGHR"
        color="error"
        icon="mdi-close"
        class="mr-2"
        :zeitbereich="uiData.zeitbereich"
        :text="
          globalsBezeichnung(
            Globals.Begriff,
            'RK_Web_SonstBelegKI_AblehnVGHR',
            'abgelehnt',
          )
        "
        variant="plain"
        :modellAic="uiData.data[0].aic_Stamm"
        @calculated="onCalculated"
      />
    </v-card-actions>
  </v-card>
</template>

<style scoped>
.dvhCard {
  height: auto;
  width: 550px;
  margin: 0px;
  padding: 0px;
  display: inline-block;
  vertical-align: top-left;
}
.rowGap {
  padding-top: 2px;
}
.dvhCol {
  padding-right: 0px;
  padding-left: 10px;
  padding-top: 0px;
  padding-bottom: 0px;
}
.cardText {
  margin-bottom: 5px;
  text-align: left;
  padding: 10px;
}
@media (max-width: 550px) {
  .v-card__subtitle,
  .v-card__text,
  .v-card__title {
    padding: 10px;
  }
}
</style>
